import { gql, useMutation, useQuery } from "@apollo/client";
import parse from "html-react-parser";
import React, { useMemo, useState } from "react";
import IconTrash from "../../../../components/icon/IconTrash";
import Countries from "../../../../models/utils/Countries";
import OldUrls from "../../../../models/utils/OldUrls";
import { Badge, Licensee, LicenseeType } from "../../../../schema";
import UILink from "../../../../ui/UILink";
import Modal from "../../../../ui/modal/Modal";
import { handleError, handleSuccess } from "../../../../utils/Apollo";
import UnexpectedErrorScene from "../../../system/UnexpectedErrorScene";
import { reverse } from "../../../../urls";

type Props = {
  licensee: Licensee;
};

export function ProfileTab({ licensee }: Props) {
  const { loading, data, error } = useQuery<BadgeQuery>(query, {
    variables: { id: licensee.id },
  });
  const [showAddBadgeModal, setShowAddBadgeModal] = useState(false);
  const [showDelete, setShowDelete] = React.useState(false);

  const onClose = () => {
    setShowDelete(false);
  };

  const [addMe] = useMutation(addMutation, {
    refetchQueries: ["BadgeQuery", "licenseeBadges"],
  });
  const [deleteMe] = useMutation(deleteMutation, {
    refetchQueries: ["BadgeQuery", "licenseeBadges"],
  });

  const onAdd = React.useCallback(
    (badgeId: string) => {
      addMe({
        variables: { licenseeId: licensee.id, id: badgeId },
      }).then(
        () => {
          setShowAddBadgeModal(false);
          handleSuccess("Badge was added");
        },
        (apolloError) => handleError(apolloError)
      );
    },
    [addMe, licensee]
  );

  const onDelete = React.useCallback(
    (badgeId: string) => {
      deleteMe({
        variables: { licenseeId: licensee.id, id: badgeId },
      }).then(
        () => {
          handleSuccess("Badge was removed");
        },
        (apolloError) => handleError(apolloError)
      );
    },
    [deleteMe, licensee]
  );

  const availableBadges = useMemo(() => {
    return data?.badges
      .filter(
        (x) => data?.licenseeBadges.findIndex((y) => y.id === x.id) === -1
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  if (error) {
    return (
      <UnexpectedErrorScene error={error} errorInfo={null} eventId={null} />
    );
  }

  return (
    <div className="grid grid-cols-3 gap-x-16 py-10">
      {!loading && !!data && (
        <>
          <div className="col-span-2">
            <h2 className="text-xl font-bold">Profile</h2>
            <div className="grid grid-cols-6 pt-4">
              {licensee.type === LicenseeType.Trainer && (
                <>
                  <div className="text-gray-dark">Bio</div>
                  <div className="col-span-5">
                    {licensee.trainer?.bio && parse(licensee.trainer?.bio)}
                    {!licensee.trainer?.bio && (
                      <UILink
                        href={OldUrls.editTrainer(licensee.trainer?.id || "0")}
                      >
                        Add bio
                      </UILink>
                    )}
                  </div>
                </>
              )}
              {licensee.type === LicenseeType.Company && (
                <>
                  <div className="text-gray-dark">About</div>
                  <div className="col-span-5">
                    {licensee.company?.description &&
                      parse(licensee.company?.description)}
                    {!licensee.company?.description && (
                      <UILink
                        href={reverse("licensee_edit_company", {
                          id: licensee.id,
                        })}
                      >
                        Add about
                      </UILink>
                    )}
                  </div>
                </>
              )}
            </div>
            {licensee.billingAddresses?.length > 0 && (
              <>
                <h2 className="text-xl font-bold mt-8">Billing addresses</h2>
                <div className="grid grid-cols-6 pt-4 gap-y-4">
                  {licensee.billingAddresses.map((address, index) => (
                    <React.Fragment key={index}>
                      <div className="text-gray-dark">#{index + 1}</div>
                      <div className="col-span-5 flex flex-col gap-y-2">
                        <div>{address.name}</div>
                        <address className="text-sm">
                          <div>{address.street1}</div>
                          {address.street2 && <div>{address.street2}</div>}
                          {address.city && <div>{address.city}</div>}
                          {address.province && <div>{address.province}</div>}
                          {address.postcode && <div>{address.postcode}</div>}
                          <div>{Countries.getName(address.country)}</div>
                        </address>
                        {address.taxNumber && (
                          <div>
                            <strong>Tax number: </strong>
                            {address.taxNumber}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold">Badges</h2>
            <div className="flex gap-4 pt-4 flex-wrap">
              {data?.licenseeBadges.map((badge) => (
                <div className="w-24 h-24 relative" key={badge.id}>
                  <img src={badge?.image} alt={badge.name} />
                  <div
                    className="absolute right-0 bottom-0 cursor-pointer bg-blue hover:bg-blue-hover rounded-full py-1 px-2"
                    onClick={() => onDelete(badge.id)}
                  >
                    <IconTrash className="text-white" />
                  </div>
                </div>
              ))}
              {!!availableBadges?.length && (
                <div
                  className="w-24 h-24 rounded-full bg-gray-light pt-6 text-center text-3xl text-blue cursor-pointer"
                  onClick={() => setShowAddBadgeModal(true)}
                >
                  +
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <Modal
        show={showAddBadgeModal}
        onClose={() => setShowAddBadgeModal(false)}
      >
        <div className="flex flex-col space-y-6">
          <h2 className="text-xl font-bold">Add Badge</h2>
          <div className="grid grid-cols-4 gap-6">
            {availableBadges?.map((badge) => (
              <div
                key={badge.id}
                className="flex flex-col flex-wrap items-center space-y-2 p-3 cursor-pointer hover:bg-blue-table-hover"
                onClick={() => onAdd(badge.id)}
              >
                <div className="w-24 h-24 relative">
                  <img src={badge.image} alt={badge.name} />
                </div>
                <div className="text-center text-gray text-sm">
                  {badge.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}

interface BadgeQuery {
  licenseeBadges: Badge[];
  badges: Badge[];
}

const query = gql`
  query BadgeQuery($id: ID!) {
    licenseeBadges: licenseeBadges(licenseeId: $id) {
      id
      name
      image
    }
    badges {
      id
      name
      image
    }
  }
`;

const addMutation = gql`
  mutation AddBadge($licenseeId: ID!, $id: ID!) {
    addLicenseeBadge(licenseeId: $licenseeId, badgeId: $id)
  }
`;

const deleteMutation = gql`
  mutation DeleteBadge($licenseeId: ID!, $id: ID!) {
    removeLicenseeBadge(licenseeId: $licenseeId, badgeId: $id)
  }
`;
